import React, { useState, useEffect } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Spinner,
    Alert,
    Row,
    Col,
    FormText,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../api/urls";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PhoneInput from "../../components/Forms/PhoneInput";
import { COMPANY_TYPES } from "../../constants/companyTypes";
import { DISTRICTS } from "../../constants/districts";
import "../../assets/css/page-common.css";
import BackButton from "../../components/Common/BackButton";

const NAVY = "#221C5D";

const CreateCompanyPage = () => {
    const navigate = useNavigate();

    const [form, setForm] = useState({
        companyName: "",
        subdomain: "",
        domain: "",
        contactFullName: "",
        companyType: "",
        workingArea: "",
        contactEmail: "",
        // Lisans tarihleri artık atanan paketten türetilir; elle girilmez.
        planId: "",
        active: true,
        phoneNumber: "",
        address: "",
        // kur ayarları
        fetchRatesAutomatically: true,
        usdMarkupRate: "",
        eurMarkupRate: "",
        gbpMarkupRate: "",
        finalUsdRate: "",
        finalEurRate: "",
        finalGbpRate: "",
        // fatura
        invoiceTaxNumber: "",
        invoiceTcNumber: "",
        invoiceTaxOffice: "",
    });

    const [districtOptions, setDistrictOptions] = useState([]);

    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [backendError, setBackendError] = useState("");

    // Paketler (trial dahil — super admin görünümü) ve seçilen pakete göre lisans önizlemesi
    const [plans, setPlans] = useState([]);
    useEffect(() => {
        axios.get(API.admin_subscription_plans)
            .then(res => setPlans(res.data?.data || []))
            .catch(() => setPlans([]));
    }, []);
    const selectedPlan = plans.find(p => String(p.id) === String(form.planId));
    const planDurationText = (p) =>
        p?.trial ? `${p.trialDays || 0} gün (deneme)` : `${p.durationMonths || 0} ay`;
    const planRangePreview = (() => {
        if (!selectedPlan) return null;
        const start = new Date();
        const end = new Date(start);
        if (selectedPlan.trial && selectedPlan.trialDays) end.setDate(end.getDate() + selectedPlan.trialDays);
        else end.setMonth(end.getMonth() + (selectedPlan.durationMonths || 0));
        const f = (d) => d.toLocaleDateString("tr-TR");
        return `${f(start)} – ${f(end)}`;
    })();

    const showToast = (type, msg) => {
        const map = { danger: "error", error: "error" };
        const fn = toast[map[type] || type] || toast.info;
        fn(msg, { autoClose: 3000 });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === "city") {
            const city = value;
            setForm((prev) => ({
                ...prev,
                city,
                district: "",
            }));
            setDistrictOptions(DISTRICTS[city] || []);
        } else {
            setForm((prev) => ({
                ...prev,
                [name]: type === "checkbox" ? checked : value,
            }));
        }

        if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
        if (backendError) setBackendError("");
    };
    // Firma tipi değişince kimlik/vergi alanlarını sıfırla
    const handleCompanyTypeChange = (e) => {
        const { value } = e.target;
        setForm((prev) => ({
            ...prev,
            companyType: value,
            invoiceTaxNumber: "",
            invoiceTcNumber: "",
        }));
        setErrors((prev) => ({
            ...prev,
            invoiceTaxNumber: "",
            invoiceTcNumber: "",
            companyType: "",
        }));
        if (backendError) setBackendError("");
    };

    const toggleBool = (key) => setForm((p) => ({ ...p, [key]: !p[key] }));

    const validateForm = () => {
        const n = {};

        // Zorunlu alanlar
        if (!form.companyName.trim()) n.companyName = "Firma adı zorunludur";
        if (!form.subdomain.trim()) n.subdomain = "Subdomain alanı zorunludur";
        if (!form.domain.trim()) n.domain = "Domain alanı zorunludur";
        if (!form.contactFullName.trim()) n.contactFullName = "İletişim kişi adı zorunludur";
        if (!form.companyType) n.companyType = "Firma tipi zorunludur";
        if (!form.workingArea) n.workingArea = "Faaliyet alanı zorunludur";
        if (!form.contactEmail.trim()) n.contactEmail = "İletişim e-posta adresi zorunludur";
        if (!form.planId) n.planId = "Paket seçimi zorunludur";

        // Uzunluk
        if (form.companyName.length > 120) n.companyName = "Firma adı en fazla 120 karakter olabilir";
        if (form.contactFullName.length > 120) n.contactFullName = "İletişim kişi adı en fazla 120 karakter olabilir";
        if (form.contactEmail.length > 120) n.contactEmail = "İletişim e-posta adresi en fazla 120 karakter olabilir";
        if (form.address.length > 255) n.address = "Adres en fazla 255 karakter olabilir";
        if (form.invoiceTaxOffice.length > 100) n.invoiceTaxOffice = "Vergi dairesi en fazla 100 karakter olabilir";

        // Patternler
        const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (form.contactEmail && !emailRe.test(form.contactEmail)) {
            n.contactEmail = "Lütfen geçerli bir e-posta adresi giriniz";
        }

        const subdomainRe = /^[a-z0-9-]{3,100}$/;
        if (form.subdomain && !subdomainRe.test(form.subdomain)) {
            n.subdomain = "Subdomain küçük harf, rakam ve tire (-) içermeli (3-100)";
        }

        const domainRe = /^[a-z0-9.-]{3,100}$/;
        if (form.domain && !domainRe.test(form.domain)) {
            n.domain = "Domain küçük harf, rakam, nokta ve tire (-) içermeli (3-100)";
        }

        if (form.phoneNumber) {
            const phoneRe = /^\+?\d{7,15}$/;
            if (!phoneRe.test(form.phoneNumber)) {
                n.phoneNumber = "Telefon + ile başlayabilir ve 7-15 rakamdan oluşmalıdır";
            }
        }

        // Firma tipine göre TC / VKN zorunlu
        const isSoleProp = form.companyType === "SOLE_PROPRIETORSHIP";

        if (isSoleProp) {
            if (!form.invoiceTcNumber) {
                n.invoiceTcNumber = "Şahıs firması için TC Kimlik numarası zorunludur";
            } else if (!/^\d{11}$/.test(form.invoiceTcNumber)) {
                n.invoiceTcNumber = "TC Kimlik numarası 11 haneli olmalıdır";
            }
            // VKN opsiyonel; dolu girilirse kontrol etmek istersen:
            if (form.invoiceTaxNumber && !/^\d{10}$/.test(form.invoiceTaxNumber)) {
                n.invoiceTaxNumber = "Vergi numarası 10 haneli olmalıdır";
            }
        } else {
            if (!form.invoiceTaxNumber) {
                n.invoiceTaxNumber = "Vergi kimlik numarası zorunludur";
            } else if (!/^\d{10}$/.test(form.invoiceTaxNumber)) {
                n.invoiceTaxNumber = "Vergi numarası 10 haneli olmalıdır";
            }
            // TC opsiyonel; doluysa format kontrolü yap
            if (form.invoiceTcNumber && !/^\d{11}$/.test(form.invoiceTcNumber)) {
                n.invoiceTcNumber = "TC kimlik numarası 11 haneli olmalıdır";
            }
        }

        // Kur alanları
        const isNumOrEmpty = (v) => v === "" || !isNaN(Number(v));
        if (form.fetchRatesAutomatically) {
            if (!isNumOrEmpty(form.usdMarkupRate)) n.usdMarkupRate = "Geçerli bir oran giriniz";
            if (!isNumOrEmpty(form.eurMarkupRate)) n.eurMarkupRate = "Geçerli bir oran giriniz";
            if (!isNumOrEmpty(form.gbpMarkupRate)) n.gbpMarkupRate = "Geçerli bir oran giriniz";
        } else {
            if (!isNumOrEmpty(form.finalUsdRate)) n.finalUsdRate = "Geçerli bir kur giriniz";
            if (!isNumOrEmpty(form.finalEurRate)) n.finalEurRate = "Geçerli bir kur giriniz";
            if (!isNumOrEmpty(form.finalGbpRate)) n.finalGbpRate = "Geçerli bir kur giriniz";
        }

        setErrors(n);
        if (Object.keys(n).length) {
            Object.values(n).forEach((m) => showToast("warning", m));
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setSaving(true);
        setBackendError("");

        try {
            const { city, district, ...restForm } = form;

            const baseAddress = restForm.address?.trim() || "";
            const ilIlcePart =
                city && district
                    ? `${district} / ${city}`
                    : city
                        ? city
                        : district || "";

            const composedAddress = baseAddress
                ? `${baseAddress} - ${ilIlcePart}`
                : ilIlcePart;

            const payload = {
                ...restForm,
                address: composedAddress,
            };

            payload.usdMarkupRate =
                payload.usdMarkupRate !== "" ? parseFloat(payload.usdMarkupRate) : null;
            payload.eurMarkupRate =
                payload.eurMarkupRate !== "" ? parseFloat(payload.eurMarkupRate) : null;
            payload.gbpMarkupRate =
                payload.gbpMarkupRate !== "" ? parseFloat(payload.gbpMarkupRate) : null;

            payload.finalUsdRate =
                payload.finalUsdRate !== "" ? parseFloat(payload.finalUsdRate) : null;
            payload.finalEurRate =
                payload.finalEurRate !== "" ? parseFloat(payload.finalEurRate) : null;
            payload.finalGbpRate =
                payload.finalGbpRate !== "" ? parseFloat(payload.finalGbpRate) : null;

            const res = await axios.post(API.admin_companies_list, payload, {
                headers: {},
            });

            if (res.status === 401) {
                navigate("/login");
                return;
            }

            toast.success("Firma başarıyla oluşturuldu!");
            setTimeout(() => navigate("/companies"), 1200);
        } catch (err) {
            if (err.response?.data) {
                const { statusCode, message } = err.response.data;
                if (statusCode === 409) {
                    if (message?.includes("Subdomain")) {
                        showToast("danger", "Bu alt domain zaten kullanımda. Lütfen farklı bir alt domain seçiniz.");
                    } else if (message?.includes("Contact e-mail")) {
                        showToast("danger", "Bu e-posta adresi zaten kullanımda. Lütfen farklı bir e-posta giriniz.");
                    } else {
                        showToast("danger", "Kayıt sırasında bir çakışma oldu.");
                    }
                } else if (statusCode === 400) {
                    showToast("danger", `Geçersiz veri: ${message}`);
                } else if (statusCode === 500) {
                    showToast("danger", "Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyiniz.");
                } else {
                    showToast("danger", message || "Firma oluşturulurken bir hata oluştu.");
                }
                setBackendError(message || "");
            } else if (err.request) {
                showToast("danger", "Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.");
            } else {
                showToast("danger", "Firma oluşturulurken bilinmeyen bir hata oluştu.");
            }
        } finally {
            setSaving(false);
        }
    };

    const turkishCities = [
        "Adana","Adıyaman","Afyonkarahisar","Ağrı","Amasya","Ankara","Antalya","Artvin",
        "Aydın","Balıkesir","Bilecik","Bingöl","Bitlis","Bolu","Burdur","Bursa","Çanakkale",
        "Çankırı","Çorum","Denizli","Diyarbakır","Edirne","Elazığ","Erzincan","Erzurum",
        "Eskişehir","Gaziantep","Giresun","Gümüşhane","Hakkâri","Hatay","Isparta","Mersin",
        "İstanbul","İzmir","Kars","Kastamonu","Kayseri","Kırklareli","Kırşehir","Kocaeli",
        "Konya","Kütahya","Malatya","Manisa","Kahramanmaraş","Mardin","Muğla","Muş",
        "Nevşehir","Niğde","Ordu","Rize","Sakarya","Samsun","Siirt","Sinop","Sivas",
        "Tekirdağ","Tokat","Trabzon","Tunceli","Şanlıurfa","Uşak","Van","Yozgat","Zonguldak",
        "Aksaray","Bayburt","Karaman","Kırıkkale","Batman","Şırnak","Bartın","Ardahan",
        "Iğdır","Yalova","Karabük","Kilis","Osmaniye","Düzce"
    ];
    const isSoleProp = form.companyType === "SOLE_PROPRIETORSHIP";

    return (
        <React.Fragment>
            <div className="page-content page-common">
                <div className="container-fluid">

                    {/* ── Page Header ── */}
                    <div className="page-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                            <BackButton />
                            <div>
                                <h5 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>
                                    Yeni Firma
                                </h5>
                                <span className="text-muted" style={{ fontSize: ".82rem" }}>
                                    Firma Oluşturma Sayfası
                                </span>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <button
                                className="btn btn-navy btn-rounded d-flex align-items-center gap-1 px-3"
                                style={{ height: 34, fontSize: ".8rem" }}
                                onClick={handleSubmit}
                                disabled={saving}
                            >
                                {saving ? (<><Spinner size="sm" className="me-1" /> Oluşturuluyor...</>) : (<><i className="bx bx-save" style={{ fontSize: 16 }} /> Firma Oluştur</>)}
                            </button>
                        </div>
                    </div>

                    <Row>
                        <Col lg={12}>
                            <Card className="detail-card">
                                <CardHeader>
                                    <div className="section-title">
                                        <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                                            <i className="bx bx-buildings" />
                                        </div>
                                        Firma Bilgileri
                                    </div>
                                </CardHeader>

                                <CardBody className="p-4">
                                    {backendError && (
                                        <Alert color="danger" className="border-0 rounded-4">
                                            <i className="bx bx-error me-2"></i>
                                            {backendError}
                                        </Alert>
                                    )}

                                    <Form onSubmit={handleSubmit}>
                                        {/* 1) Firma Tipi & Durum */}
                                        <div
                                            className="border rounded-4 p-4 mb-4"
                                            style={{ backgroundColor: "#f9f9f9" }}
                                        >
                                            <h6 className="mb-3 fw-semibold">Firma Tipi ve Durum</h6>
                                            <Row className="g-3">
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">
                                                            Firma Tipi <span className="text-danger">*</span>
                                                        </Label>
                                                        <Input
                                                            type="select"
                                                            name="companyType"
                                                            value={form.companyType}
                                                            onChange={handleCompanyTypeChange}
                                                            invalid={!!errors.companyType}
                                                            className="rounded-3"
                                                        >
                                                            <option value="">Tip seçiniz</option>
                                                            {COMPANY_TYPES.map((t) => (
                                                                <option key={t.value} value={t.value}>
                                                                    {t.label}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                        <small className="text-muted">
                                                            {isSoleProp
                                                                ? "Şahıs firması için TC Kimlik zorunludur."
                                                                : "Diğer firma tipleri için Vergi Kimlik No zorunludur."}
                                                        </small>
                                                    </FormGroup>
                                                </Col>

                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Firma Durumu</Label>
                                                        <div className="d-flex align-items-center">
                                                            <div
                                                                className="toggle-switch"
                                                                onClick={() => toggleBool("active")}
                                                                style={{
                                                                    width: 50,
                                                                    height: 24,
                                                                    backgroundColor: form.active ? "#28a745" : "#6c757d",
                                                                    borderRadius: 12,
                                                                    position: "relative",
                                                                    cursor: "pointer",
                                                                    transition: "background-color 0.2s",
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: 20,
                                                                        height: 20,
                                                                        backgroundColor: "white",
                                                                        borderRadius: "50%",
                                                                        position: "absolute",
                                                                        top: 2,
                                                                        left: form.active ? 28 : 2,
                                                                        transition: "left 0.2s",
                                                                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                                                    }}
                                                                />
                                                            </div>
                                                            <span className="ms-3">
                                                                <strong>{form.active ? "Aktif" : "Pasif"}</strong>
                                                                <br />
                                                                <small className="text-muted">
                                                                    {form.active ? "Şirket aktif" : "Şirket devre dışı"}
                                                                </small>
                                                            </span>
                                                        </div>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* 2) Temel Bilgiler */}
                                        <div
                                            className="border rounded-4 p-4 mb-4"
                                            style={{ backgroundColor: "#f9f9f9" }}
                                        >
                                            <h6 className="mb-3 fw-semibold">Temel Bilgiler</h6>
                                            <Row className="g-3">
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">
                                                            Firma Adı <span className="text-danger">*</span>
                                                        </Label>
                                                        <Input
                                                            name="companyName"
                                                            value={form.companyName}
                                                            onChange={handleChange}
                                                            maxLength={120}
                                                            invalid={!!errors.companyName}
                                                            className="rounded-3"
                                                            placeholder="Örnek Firma"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={3}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">
                                                            Alt Domain <span className="text-danger">*</span>
                                                        </Label>
                                                        <Input
                                                            name="subdomain"
                                                            value={form.subdomain}
                                                            onChange={handleChange}
                                                            maxLength={100}
                                                            invalid={!!errors.subdomain}
                                                            className="rounded-3"
                                                            placeholder="orneksirket"
                                                        />
                                                        <FormText>
                                                            Alt alan adı 3–100 karakter olmalı; küçük harf, rakam ve tire (-) içerebilir.
                                                            Belirttiğiniz alt alan adı tahsilat360.com’un önüne eklenerek <strong>test.tahsilat360.com</strong> formatında kullanılır.
                                                        </FormText>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={3}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">
                                                            Domain <span className="text-danger">*</span>
                                                        </Label>
                                                        <Input
                                                            name="domain"
                                                            value={form.domain}
                                                            onChange={handleChange}
                                                            maxLength={100}
                                                            invalid={!!errors.domain}
                                                            className="rounded-3"
                                                            placeholder="ornek.com"
                                                        />
                                                        <FormText>harf, rakam, nokta, tire (3-100)</FormText>
                                                    </FormGroup>
                                                </Col>

                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">
                                                            Faaliyet Alanı <span className="text-danger">*</span>
                                                        </Label>
                                                        <Input
                                                            type="select"
                                                            name="workingArea"
                                                            value={form.workingArea}
                                                            onChange={handleChange}
                                                            invalid={!!errors.workingArea}
                                                            className="rounded-3"
                                                        >
                                                            <option value="">Alan seçiniz</option>
                                                            <option value="INFORMATION_TECHNOLOGY">Bilişim ve Yazılım</option>
                                                            <option value="MANUFACTURING">Üretim ve İmalat</option>
                                                            <option value="RETAIL">Perakende Satış</option>
                                                            <option value="HEALTHCARE">Sağlık ve Tıp</option>
                                                            <option value="FINANCE">Finans ve Sigorta</option>
                                                            <option value="EDUCATION">Eğitim Hizmetleri</option>
                                                            <option value="AUTOMOTIVE">Otomotiv Sektörü</option>
                                                            <option value="FOOD_AND_BEVERAGE">Gıda ve İçecek</option>
                                                            <option value="CONSTRUCTION">İnşaat Sektörü</option>
                                                            <option value="LOGISTICS">Lojistik ve Nakliye</option>
                                                            <option value="ENERGY">Enerji</option>
                                                            <option value="OTHER">Diğer</option>
                                                        </Input>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={3}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Paket <span className="text-danger">*</span></Label>
                                                        <Input
                                                            type="select"
                                                            name="planId"
                                                            value={form.planId}
                                                            onChange={handleChange}
                                                            className="rounded-3"
                                                            invalid={!!errors.planId}
                                                        >
                                                            <option value="">Paket seçiniz</option>
                                                            {plans.map((p) => (
                                                                <option key={p.id} value={p.id}>
                                                                    {p.name} — {planDurationText(p)}{p.trial ? " • gizli" : ""}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={3}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Lisans Aralığı (pakete göre)</Label>
                                                        <div
                                                            className="rounded-3 border px-3 d-flex align-items-center"
                                                            style={{ height: 38, background: "#f8fafc", fontSize: ".85rem", color: planRangePreview ? "#0A316E" : "#94a3b8" }}
                                                        >
                                                            <i className="bx bx-calendar me-2" />
                                                            {planRangePreview || "Paket seçince hesaplanır"}
                                                        </div>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* 3) İletişim Bilgileri */}
                                        <div
                                            className="border rounded-4 p-4 mb-4"
                                            style={{ backgroundColor: "#f9f9f9" }}
                                        >
                                            <h6 className="mb-3 fw-semibold">İletişim Bilgileri</h6>
                                            <Row className="g-3">
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">
                                                            İletişim Kişisi <span className="text-danger">*</span>
                                                        </Label>
                                                        <Input
                                                            name="contactFullName"
                                                            value={form.contactFullName}
                                                            onChange={handleChange}
                                                            maxLength={120}
                                                            invalid={!!errors.contactFullName}
                                                            className="rounded-3"
                                                            placeholder="Ad Soyad"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">
                                                            İletişim E-postası <span className="text-danger">*</span>
                                                        </Label>
                                                        <Input
                                                            type="email"
                                                            name="contactEmail"
                                                            value={form.contactEmail}
                                                            onChange={handleChange}
                                                            maxLength={120}
                                                            invalid={!!errors.contactEmail}
                                                            className="rounded-3"
                                                            placeholder="ornek@firma.com"
                                                        />
                                                        <FormText>Bildirimler bu adrese gönderilir.</FormText>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <PhoneInput
                                                            value={form.phoneNumber}
                                                            onChange={handleChange}
                                                            name="phoneNumber"
                                                        />
                                                        {errors.phoneNumber && (
                                                            <div className="invalid-feedback d-block">
                                                                {errors.phoneNumber}
                                                            </div>
                                                        )}
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* 4) Adres & Fatura Bilgileri (sadece tek adres) */}
                                        <div
                                            className="border rounded-4 p-4 mb-4"
                                            style={{ backgroundColor: "#f9f9f9" }}
                                        >
                                            <h6 className="mb-3 fw-semibold">Adres ve Fatura Bilgileri</h6>
                                            <Row className="g-3">

                                                {/* Şehir */}
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Şehir</Label>
                                                        <Input
                                                            type="select"
                                                            name="city"
                                                            value={form.city}
                                                            onChange={handleChange}
                                                            className="rounded-3"
                                                        >
                                                            <option value="">Seçiniz</option>
                                                            {turkishCities.map((city) => (
                                                                <option key={city} value={city}>
                                                                    {city}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>

                                                {/* İlçe */}
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">İlçe</Label>
                                                        <Input
                                                            type="select"
                                                            name="district"
                                                            value={form.district}
                                                            onChange={handleChange}
                                                            className="rounded-3"
                                                            disabled={!form.city}
                                                        >
                                                            <option value="">İlçe seçiniz</option>
                                                            {districtOptions.map((dist) => (
                                                                <option key={dist} value={dist}>
                                                                    {dist}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>

                                                {/* Adres */}
                                                <Col md={12}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Adres</Label>
                                                        <Input
                                                            type="textarea"
                                                            name="address"
                                                            value={form.address}
                                                            onChange={handleChange}
                                                            maxLength={255}
                                                            rows={3}
                                                            invalid={!!errors.address}
                                                            className="rounded-3"
                                                            placeholder="Mahalle, Cadde, No, Kapı vb."
                                                        />
                                                    </FormGroup>
                                                </Col>

                                                {/* Firma tipine göre TC / VKN alanları */}
                                                {isSoleProp ? (
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label className="small text-muted mb-1">
                                                                TC Kimlik No <span className="text-danger">*</span>
                                                            </Label>
                                                            <Input
                                                                name="invoiceTcNumber"
                                                                value={form.invoiceTcNumber}
                                                                onChange={handleChange}
                                                                maxLength={11}
                                                                inputMode="numeric"
                                                                pattern="[0-9]*"
                                                                onInput={(e) =>
                                                                    (e.target.value = e.target.value.replace(/\D/g, ""))
                                                                }
                                                                invalid={!!errors.invoiceTcNumber}
                                                                className="rounded-3"
                                                                placeholder="11 haneli TC kimlik numarası"
                                                            />
                                                            {errors.invoiceTcNumber && (
                                                                <div className="invalid-feedback d-block">
                                                                    {errors.invoiceTcNumber}
                                                                </div>
                                                            )}
                                                        </FormGroup>
                                                    </Col>
                                                ) : (
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label className="small text-muted mb-1">
                                                                Vergi Kimlik No <span className="text-danger">*</span>
                                                            </Label>
                                                            <Input
                                                                name="invoiceTaxNumber"
                                                                value={form.invoiceTaxNumber}
                                                                onChange={handleChange}
                                                                maxLength={10}
                                                                inputMode="numeric"
                                                                pattern="[0-9]*"
                                                                onInput={(e) =>
                                                                    (e.target.value = e.target.value.replace(/\D/g, ""))
                                                                }
                                                                invalid={!!errors.invoiceTaxNumber}
                                                                className="rounded-3"
                                                                placeholder="10 haneli vergi kimlik numarası"
                                                            />
                                                            {errors.invoiceTaxNumber && (
                                                                <div className="invalid-feedback d-block">
                                                                    {errors.invoiceTaxNumber}
                                                                </div>
                                                            )}
                                                        </FormGroup>
                                                    </Col>
                                                )}

                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Vergi Dairesi</Label>
                                                        <Input
                                                            name="invoiceTaxOffice"
                                                            value={form.invoiceTaxOffice}
                                                            onChange={handleChange}
                                                            maxLength={100}
                                                            invalid={!!errors.invoiceTaxOffice}
                                                            className="rounded-3"
                                                            placeholder="Merkez Vergi D."
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* 5) Döviz Kuru Ayarları */}
                                        <div
                                            className="border rounded-4 p-4 mb-4"
                                            style={{ backgroundColor: "#f9f9f9" }}
                                        >
                                            <h6 className="mb-3 fw-semibold">Döviz Kuru Ayarları</h6>

                                            <div className="d-flex align-items-center mb-3">
                                                <div
                                                    className="toggle-switch"
                                                    onClick={() => toggleBool("fetchRatesAutomatically")}
                                                    style={{
                                                        width: 50,
                                                        height: 24,
                                                        backgroundColor: form.fetchRatesAutomatically ? "#0A316E" : "#6c757d",
                                                        borderRadius: 12,
                                                        position: "relative",
                                                        cursor: "pointer",
                                                        transition: "background-color 0.2s",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: 20,
                                                            height: 20,
                                                            backgroundColor: "white",
                                                            borderRadius: "50%",
                                                            position: "absolute",
                                                            top: 2,
                                                            left: form.fetchRatesAutomatically ? 28 : 2,
                                                            transition: "left 0.2s",
                                                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                                        }}
                                                    />
                                                </div>
                                                <span className="ms-3">
                                                    <strong>
                                                        {form.fetchRatesAutomatically
                                                            ? "Otomatik (TCMB + Oran)"
                                                            : "Manuel (Final Kurları)"}
                                                    </strong>
                                                    <br />
                                                    <small className="text-muted">
                                                        {form.fetchRatesAutomatically
                                                            ? "TCMB üzeri ekleme oranı ile hesaplanır"
                                                            : "Müşteriye yansıyacak kur değerlerini giriniz"}
                                                    </small>
                                                </span>
                                            </div>

                                            {form.fetchRatesAutomatically ? (
                                                <Row className="g-3">
                                                    <Col md={4}>
                                                        <FormGroup>
                                                            <Label className="small text-muted mb-1">USD Ekleme Oranı (%)</Label>
                                                            <Input
                                                                type="number"
                                                                step="0.001"
                                                                name="usdMarkupRate"
                                                                value={form.usdMarkupRate}
                                                                onChange={handleChange}
                                                                invalid={!!errors.usdMarkupRate}
                                                                className="rounded-3"
                                                                placeholder="Örn: 0.03"
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={4}>
                                                        <FormGroup>
                                                            <Label className="small text-muted mb-1">EUR Ekleme Oranı (%)</Label>
                                                            <Input
                                                                type="number"
                                                                step="0.001"
                                                                name="eurMarkupRate"
                                                                value={form.eurMarkupRate}
                                                                onChange={handleChange}
                                                                invalid={!!errors.eurMarkupRate}
                                                                className="rounded-3"
                                                                placeholder="Örn: 0.03"
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={4}>
                                                        <FormGroup>
                                                            <Label className="small text-muted mb-1">GBP Ekleme Oranı (%)</Label>
                                                            <Input
                                                                type="number"
                                                                step="0.001"
                                                                name="gbpMarkupRate"
                                                                value={form.gbpMarkupRate}
                                                                onChange={handleChange}
                                                                invalid={!!errors.gbpMarkupRate}
                                                                className="rounded-3"
                                                                placeholder="Örn: 0.03"
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                            ) : (
                                                <Row className="g-3">
                                                    <Col md={4}>
                                                        <FormGroup>
                                                            <Label className="small text-muted mb-1">USD Final Kuru</Label>
                                                            <Input
                                                                type="number"
                                                                step="0.00001"
                                                                name="finalUsdRate"
                                                                value={form.finalUsdRate}
                                                                onChange={handleChange}
                                                                invalid={!!errors.finalUsdRate}
                                                                className="rounded-3"
                                                                placeholder="Örn: 32.85"
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={4}>
                                                        <FormGroup>
                                                            <Label className="small text-muted mb-1">EUR Final Kuru</Label>
                                                            <Input
                                                                type="number"
                                                                step="0.00001"
                                                                name="finalEurRate"
                                                                value={form.finalEurRate}
                                                                onChange={handleChange}
                                                                invalid={!!errors.finalEurRate}
                                                                className="rounded-3"
                                                                placeholder="Örn: 35.12"
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={4}>
                                                        <FormGroup>
                                                            <Label className="small text-muted mb-1">GBP Final Kuru</Label>
                                                            <Input
                                                                type="number"
                                                                step="0.00001"
                                                                name="finalGbpRate"
                                                                value={form.finalGbpRate}
                                                                onChange={handleChange}
                                                                invalid={!!errors.finalGbpRate}
                                                                className="rounded-3"
                                                                placeholder="Örn: 41.50"
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                            )}
                                        </div>

                                        {/* Aksiyonlar */}
                                        <div className="d-flex justify-content-end gap-2 mt-2">
                                            <button type="button" className="btn btn-light btn-rounded px-4" style={{ height: 34, fontSize: ".8rem" }} onClick={() => navigate(-1)} disabled={saving}>
                                                İptal
                                            </button>
                                            <button type="submit" className="btn btn-navy btn-rounded px-4 d-flex align-items-center gap-1" style={{ height: 34, fontSize: ".8rem" }} disabled={saving}>
                                                {saving ? (<><Spinner size="sm" className="me-1" /> Oluşturuluyor...</>) : (<><i className="bx bx-save" style={{ fontSize: 16 }} /> Firma Oluştur</>)}
                                            </button>
                                        </div>
                                    </Form>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </React.Fragment>
    );
};

export default CreateCompanyPage;